import { Autocomplete, Chip, TextField } from '@mui/material'
import { Dispatch, memo, SetStateAction, useCallback } from 'react'

import type { QuestionTag } from '@/types'

interface TagAutocompleteProps {
  value: QuestionTag[]
  label: string
  setValue: Dispatch<SetStateAction<QuestionTag[]>>
  placeholder: string
  options?: any[]
  getOptionLabel?: (option: any) => string
  loading?: boolean
}

function TagAutocomplete({
  value,
  setValue,
  placeholder,
  label,
  options = [],
  getOptionLabel,
  loading = false,
}: TagAutocompleteProps) {
  const handleChange = useCallback(
    (_: React.SyntheticEvent, newValue: (string | QuestionTag)[]) => {
      setValue(
        newValue
          .map((item) =>
            typeof item === 'string'
              ? { id: Date.now() + Math.random(), name: item.trim() }
              : item,
          )
          .filter((item) => item.name.length > 0),
      )
    },
    [setValue],
  )

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault()
        const target = e.target as HTMLInputElement
        const inputValue = target.value.trim()

        if (inputValue && !value.some((tag) => tag.name === inputValue)) {
          setValue((prev) => [
            ...prev,
            {
              id: Date.now() + Math.random(),
              name: inputValue,
            },
          ])
          target.value = ''
        }
      }
    },
    [value, setValue],
  )

  return (
    <Autocomplete
      multiple
      freeSolo
      value={value}
      options={options}
      loading={loading}
      getOptionKey={(option) => (option as QuestionTag).id}
      onChange={handleChange}
      onKeyDown={handleKeyDown}
      getOptionLabel={(option) =>
        typeof option === 'string'
          ? option
          : getOptionLabel
            ? getOptionLabel(option)
            : option.name
      }
      isOptionEqualToValue={(option, selectedValue) =>
        typeof option === 'string'
          ? option === selectedValue.name
          : option.id === selectedValue.id
      }
      renderTags={(tagValue, getTagProps) =>
        tagValue.map((option, index) => (
          <Chip
            {...getTagProps({ index })}
            key={typeof option === 'string' ? option : option.id}
            label={typeof option === 'string' ? option : option.name}
            color="primary"
            variant="outlined"
            size="small"
            sx={{ borderRadius: 2 }}
          />
        ))
      }
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={value.length === 0 ? placeholder : ''}
          helperText={`${value.length} tag${value.length !== 1 ? 's' : ''} adicionada${value.length !== 1 ? 's' : ''}.`}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
            },
          }}
        />
      )}
    />
  )
}

export default memo(TagAutocomplete)
